import {useNavigate, useParams} from "react-router-dom";
import {useEffect} from "react";
import api from "../http";
import {useStore} from "../contexts/AppContext";
import DocumentInfo from "./DocumentInfo";
import DocumentAccess from "./DocumentAccess";
import {useFetchData} from "../hooks/useFetchData";
import {useAuth} from "../contexts/AuthContext";
import * as analytics from "../utils/Analytics";
import DocumentHistory from "./DocumentHistory";
import {Badge, Button, Card, message, Popconfirm, Tabs} from "antd";
import {accessRequestForm, items, statusObj} from "../utils/constants"
import AccessRequestForm from "./AccessRequestForm";
import {onRemoveRequest} from "../utils/onRemoveRequest";


const DocumentFullPage = () => {
    const {
        tab,
        setTab,
        newRowLoad,
        setNewRoadLoad,
        initialRow,
        setInitialRow,
        dataLoading,
        row,
        setRow,
        changed,
        setChanged,
        isForbidden,
        setIsForbidden,
        requestAccessData,
        setRequestAccessData,
    } = useStore()
    const fetchData = useFetchData();
    const {documentId} = useParams();
    const {userInfo} = useAuth()
    const navigate = useNavigate()
    const prevPage = localStorage.getItem("prevPage") || "dashboard/all-documents"
    const isIamViewer = row.current_roles_json.some(user => user.profile_id === userInfo.profile_id && user.role_id === 4)
    const isAccessDenied = row.current_roles_json.some(user => user.profile_id === userInfo.profile_id && user.role_id === 0)



    useEffect(() => {
        const abortController = new AbortController();

        const docData = async () => {
            try {
                const accessRequestResponse = await api.get(`support/access-request/${documentId}`, {
                    signal: abortController.signal
                });
                if (accessRequestResponse.status === 200 && (!accessRequestResponse.data.found || accessRequestResponse.data.request.status === 'approved')){
                    setIsForbidden(false)
                    setRequestAccessData((prevPage => ({
                        ...prevPage,
                        access_form_mode: 'createNew'
                    })))
                    const documentResponse = await api.get(`supabase/document/${documentId}`, {
                        signal: abortController.signal
                    });
                    if (documentResponse.status === 200 && !abortController.signal.aborted) {
                        setRow(documentResponse.data);
                        setInitialRow(documentResponse.data)
                        await fetchData(abortController.signal);
                    }
                }

                else if (accessRequestResponse.status === 200
                    && accessRequestResponse.data.found
                    && (accessRequestResponse.data.request.status === 'pending' || accessRequestResponse.data.request.status === 'rejected')){
                    setIsForbidden(true)
                    setRequestAccessData((prevPage => ({
                        ...prevPage,
                        access_form_mode: 'editOld',
                        access_form: accessRequestResponse.data.request
                    })))
                }
            } catch (e) {
                if (e.status === 403) {
                    setIsForbidden(true)
                    return;
                }
                if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
                    console.log(e)
                }
            }
        }
        docData()
        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line
    }, [documentId]);


    useEffect(() => {
        if (!initialRow || !row) {
            setChanged(false);
        } else {
            setChanged(JSON.stringify(initialRow) !== JSON.stringify(row));
        }
    }, [row, initialRow, setChanged]);


    const changeRow = async () => {
        setNewRoadLoad(true)
        try {
            const accessChanged = JSON.stringify(initialRow?.current_roles_json || []) !== JSON.stringify(row.current_roles_json || []);
            const response = await api.put(`/supabase/my-documents/${row.id}`, row);
            if (response.status === 200 && response.data) {
                if (row.department_id) {
                    localStorage.setItem("department_id", row.department_id)
                }
                navigate('/dashboard/all-documents')


                analytics.sendEvent('doc_modified', {doc_id: row.id, doc_url: row?.document_url || null});
                if (accessChanged) {
                    analytics.sendEvent('doc_access_modified', {
                        doc_id: row.id,
                        doc_url: row?.document_url || null
                    });
                }
            }
        } catch (e) {
            const msg = e?.response?.data;
            if (typeof msg === 'string' && /Document is public! Close access to your Google Document/i.test(msg)) {
                analytics.sendEvent('doc_is_public_warning', {
                    doc_id: row?.id || null,
                    doc_url: row?.document_url || null
                });
            }
            message.error(msg.message)
        } finally {
            setNewRoadLoad(false)
        }
    }


    const changeDocStatus = async (row) => {
        let responseData
        try {
            if (row.is_archived) {
                responseData = await api.put(`supabase/unarchive-doc/${row.id}`)
            }
            if (!row.is_archived) {
                responseData = await api.put(`supabase/archive-doc/${row.id}`)
            }

            if (responseData.status === 200 && responseData.data.status) {
                navigate('/dashboard/all-documents')
            }
        } catch (e) {
            message.error(e.response.data.message)
        }
    }

    const onConfirm = () => {
        changeRow().then(r => message.success('Changes applied'))
    }

    const onCancel = () => {
        message.error('Changes rejected');
    }

    const onClickBack = () => {
        setTab("info")
        setRequestAccessData((prevPage => ({
            ...prevPage,
            access_form: accessRequestForm,
            access_form_modal: false
        })))
        navigate(prevPage);
    }

    const onOpenAccessRequestModal = () => {
        return setRequestAccessData((prevPage => ({
            ...prevPage,
            access_accept_access_mode: 'request',
            access_form_modal: true,
            access_form_mode: requestAccessData.access_form.status === 'pending' ? 'editOld' : 'createNew',
        })))
    }



    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                width: '100%',
                padding: '20px',
                boxSizing: 'border-box'
            }}
        >
            {!isForbidden ?
                <Card
                    direction={"column"}
                    style={{
                        width: 800,
                        height: "100%",
                        overflowY: 'auto',
                    }}
                    loading={dataLoading}
                >
                    <div style={{display: "flex", gap: 10, padding: 10, justifyContent: "end"}}>
                        <Button onClick={onClickBack} appearance="subtle">
                            Back
                        </Button>
                        {userInfo.email === row.current_roles_json.find(user => user.role_id === 1)?.email &&
                            <Button
                                color={row.is_archived ? "blue" : "red"}
                                appearance="primary"
                                onClick={() => changeDocStatus(row)}>
                                {row.is_archived ? "Unarchive" : "Archive"}
                            </Button>
                        }
                        <Popconfirm
                            title="Warning"
                            description={"Save changes?"}
                            placement="topRight"
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                disabled={!changed}
                                loading={newRowLoad}>
                                Save Changes
                            </Button>
                        </Popconfirm>
                    </div>
                    <Tabs
                        items={items}
                        type="card"
                        onChange={(key) => setTab(key)}
                    />
                    {tab === "info" &&
                        <DocumentInfo
                            isIamViewer={isIamViewer}
                            isAccessDenied={isAccessDenied}
                        />
                    }
                    {tab === "access" &&
                        <DocumentAccess
                            isIamViewer={isIamViewer}
                            isAccessDenied={isAccessDenied}
                        />
                    }
                    {tab === "history" &&
                        <DocumentHistory/>
                    }
                </Card>
                :
                <Card>
                    <p>You do not have access to the document you are trying to open</p>
                    {requestAccessData.access_form.status &&
                        <p>
                            Review status - {requestAccessData.access_form.status} <Badge status={statusObj[requestAccessData.access_form.status]}/>
                        </p>
                    }
                    <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                        <div style={{display: "flex", gap: 10}}>
                            <Button
                                color="default"
                                variant="filled"
                                style={{flex: 1}}
                                onClick={onClickBack}>
                                Back
                            </Button>
                            {requestAccessData.access_form?.status === 'pending' &&
                                <Button
                                    style={{flex: 1}}
                                    key="decline"
                                    type="primary"
                                    danger
                                    onClick={() => onRemoveRequest(setRequestAccessData, setIsForbidden, documentId)}
                                >
                                    Remove
                                </Button>
                            }
                        </div>
                        <Button
                            type="primary"
                            onClick={onOpenAccessRequestModal}
                        >
                            {requestAccessData.access_form.status === "pending" ? "Update request" : "Send request"}
                        </Button>
                    </div>
                </Card>
            }
            <AccessRequestForm />
        </div>

    )
}

export default DocumentFullPage;